import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Document, Page, pdfjs } from 'react-pdf';
import 'react-pdf/dist/Page/AnnotationLayer.css';
import 'react-pdf/dist/Page/TextLayer.css';
import { ArrowLeft, Download, Printer, Loader2, FileText, RefreshCw, ZoomIn, ZoomOut, ChevronLeft, ChevronRight, Palette, ExternalLink } from 'lucide-react';

pdfjs.GlobalWorkerOptions.workerSrc = `https://unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const REPORT_LABELS = {
  quotation: 'Quotation',
  appraisal: 'Appraisal',
  materials: 'Materials Breakdown',
  profit: 'Profit Analysis',
  'quote-calcs': 'Quote Calculations',
  'quote-calcs-customer': 'Detailed Quote (Customer)',
  'quotation-detailed': 'Quotation Letter',
  'terms-of-trade': 'Terms of Trade',
  'terms-of-trade-simple': 'Terms of Trade (Simple — 8 clause)',
  'client-breakdown': 'Client Breakdown',
  'certificate-of-completion': 'Certificate of Completion',
  'cover-letter': 'Cover Letter',
  'appraisal-authorisation': 'Appraisal Authorisation Letter',
  'job-tracking-sheet': 'Appraisal Tracking Sheet',
  'work-sheet': 'Work Sheet',
  'materials-report': 'Materials Report',
  'contractor-detailed': 'Office Contractor Sheet (all trades)',
  'contractor-sheet': 'Contractor Sheet (per contractor)',
  'contractor-concrete': 'CONCRETE Contractor',
  'contractor-iron':     'IRON Contractor',
  'contractor-gutter':   'GUTTER Contractor',
  'contractor-painter':  'PAINTER Contractor',
  'property-appraisal-form': 'Property Appraisal Form',
  'roof-diagram-appraisal': 'Roof Diagram Appraisal Pack',
  invoice: 'Invoice',
  'gst-form': 'GST101A Return',
};

export default function PdfViewerPage() {
  const { type, id } = useParams();
  const { api } = useAuth();
  const navigate = useNavigate();
  const containerRef = useRef(null);

  const [pdfData, setPdfData] = useState(null);
  const [filename, setFilename] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [numPages, setNumPages] = useState(null);
  const [pageNumber, setPageNumber] = useState(1);
  const [scale, setScale] = useState(1.2);
  // Wave B1 (2026-02) — Quote Calcs audience toggle. Backend model uses a
  // single report_type with `?audience=internal|customer`; toggle stays
  // client-side so we don't need to change the URL.
  const [quoteCalcsAudience, setQuoteCalcsAudience] = useState('internal');
  // Wave B2 (2026-02) — Terms of Trade version toggle (full ↔ simple).
  // These are two distinct backend report types; we swap the fetch type
  // without navigating so refresh keeps the operator's choice.
  const [termsVariant, setTermsVariant] = useState('full');

  // Effective type sent to backend after applying toggles.
  const effectiveType = (
    type === 'terms-of-trade' && termsVariant === 'simple'
      ? 'terms-of-trade-simple'
      : type
  );

  const loadPdf = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      let url;
      // Forward any extra query params (e.g. ?contractor_id=…) from the URL
      // straight through to the backend so per-contractor pre-fill works.
      const passthroughQs = new URLSearchParams(window.location.search);
      const extra = passthroughQs.toString();
      if (type === 'invoice') {
        url = `/invoices/${id}/pdf-data`;
      } else if (type === 'gst-form') {
        // id encodes period_start_period_end e.g. "2024-04-01_2025-03-31"
        const [ps, pe] = id.split('_');
        url = `/tax-forms/gst101a/pdf-data?period_start=${ps}&period_end=${pe}`;
      } else {
        // Wave B1: quote-calcs audience toggle drives the query param.
        // Wave B2: terms-of-trade version toggle swaps report_type.
        // Legacy `quote-calcs-customer` type still routes for URL back-compat.
        const isQuoteCalcs = type === 'quote-calcs' || type === 'quote-calcs-customer';
        const legacyCustomer = type === 'quote-calcs-customer';
        const audience = legacyCustomer ? 'customer' : quoteCalcsAudience;
        if (isQuoteCalcs) {
          url = `/reports/pdf-data/quote-calcs/${id}?audience=${audience}${extra ? `&${extra}` : ''}`;
        } else {
          url = `/reports/pdf-data/${effectiveType}/${id}${extra ? `?${extra}` : ''}`;
        }
      }
      const { data } = await api.get(url);
      setPdfData(`data:application/pdf;base64,${data.pdf_base64}`);
      setFilename(data.filename || `RTR_${effectiveType}_${id.slice(-6)}.pdf`);
    } catch (e) {
      setError(e?.response?.data?.detail || e?.message || 'Failed to load PDF');
    }
    setLoading(false);
  }, [api, type, id, quoteCalcsAudience, effectiveType]);

  useEffect(() => { loadPdf(); }, [loadPdf]);

  const onDocumentLoadSuccess = ({ numPages }) => {
    setNumPages(numPages);
    setPageNumber(1);
  };

  // Build a fresh Blob from the cached base64 each time. Returning both the
  // blob and a one-shot Object URL keeps the caller free to revoke when done.
  const buildBlob = () => {
    if (!pdfData) return null;
    const byteChars = atob(pdfData.split(',')[1]);
    const bytes = new Uint8Array(byteChars.length);
    for (let i = 0; i < byteChars.length; i++) bytes[i] = byteChars.charCodeAt(i);
    return new Blob([bytes], { type: 'application/pdf' });
  };

  const handleDownload = () => {
    const blob = buildBlob();
    if (!blob) return;
    const url = URL.createObjectURL(blob);
    // The download must be triggered from a real, in-DOM <a> element with a
    // synchronous click in the same user-gesture tick — Chrome/Edge silently
    // ignore programmatic clicks on detached anchors, which is why the old
    // a.click() pattern was a no-op for the user.
    const a = document.createElement('a');
    a.style.display = 'none';
    a.href = url;
    a.download = filename || `RTR_${type}_${id.slice(-6)}.pdf`;
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    setTimeout(() => URL.revokeObjectURL(url), 30000);
  };

  // Native browser print via a hidden same-origin iframe. Avoids popup
  // blockers (which were eating the previous window.open call) and lets the
  // browser apply its own print dialog with the live PDF document.
  const handlePrint = () => {
    const blob = buildBlob();
    if (!blob) return;
    const url = URL.createObjectURL(blob);
    let frame = document.getElementById('rtr-pdf-print-frame');
    if (frame) document.body.removeChild(frame);
    frame = document.createElement('iframe');
    frame.id = 'rtr-pdf-print-frame';
    frame.style.position = 'fixed';
    frame.style.right = '0';
    frame.style.bottom = '0';
    frame.style.width = '0';
    frame.style.height = '0';
    frame.style.border = '0';
    frame.src = url;
    frame.onload = () => {
      try {
        frame.contentWindow.focus();
        frame.contentWindow.print();
      } catch (e) {
        // Fallback: open the blob in a tab so the user can print from there.
        window.open(url, '_blank', 'noopener');
      }
    };
    document.body.appendChild(frame);
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  };

  // Open the PDF in a new browser tab so the operator can preview the
  // document exactly as the client will see it (no app chrome).
  const handlePreview = () => {
    const blob = buildBlob();
    if (!blob) return;
    const url = URL.createObjectURL(blob);
    const win = window.open(url, '_blank', 'noopener');
    if (!win) {
      // Popup blocked → fall back to navigating the current tab.
      window.location.href = url;
    }
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  };

  const label = REPORT_LABELS[effectiveType] || effectiveType;
  const showAudienceToggle = type === 'quote-calcs' || type === 'quote-calcs-customer';
  const showTermsToggle    = type === 'terms-of-trade';

  return (
    <div data-testid="pdf-viewer-page" className="h-[calc(100vh-64px)] flex flex-col bg-slate-50">
      {/* Toolbar */}
      <div className="flex items-center justify-between px-4 py-2 bg-white border-b shadow-sm flex-shrink-0">
        <div className="flex items-center gap-3">
          <button data-testid="pdf-viewer-back" onClick={() => navigate(-1)} className="flex items-center gap-1.5 text-sm text-slate-600 hover:text-slate-900 transition-colors">
            <ArrowLeft size={16} /> Back
          </button>
          <div className="h-5 w-px bg-slate-200" />
          <FileText size={16} className="text-[#D35400]" />
          <span className="text-sm font-semibold text-slate-800">{label} Report</span>
          {numPages && (
            <span className="text-xs text-slate-400 ml-2">({numPages} page{numPages > 1 ? 's' : ''})</span>
          )}
          {/* Wave B1 (2026-02) — Quote Calcs audience toggle */}
          {showAudienceToggle && (
            <div className="ml-3 flex items-center gap-0 rounded-md border border-slate-200 overflow-hidden" data-testid="quote-calcs-audience-toggle">
              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500 px-2">Audience:</span>
              <button
                data-testid="quote-calcs-audience-internal"
                onClick={() => setQuoteCalcsAudience('internal')}
                className={`text-xs px-3 py-1 transition-all ${quoteCalcsAudience === 'internal' ? 'bg-[#6B46C1] text-white' : 'text-slate-600 hover:bg-slate-50'}`}
                title="12-column detailed view with Buy / Contractor / RTR Profit"
              >Internal</button>
              <button
                data-testid="quote-calcs-audience-customer"
                onClick={() => setQuoteCalcsAudience('customer')}
                className={`text-xs px-3 py-1 transition-all ${quoteCalcsAudience === 'customer' ? 'bg-amber-600 text-white' : 'text-slate-600 hover:bg-slate-50'}`}
                title="8-column client-safe view (hides Buy / Contractor / Profit)"
              >Customer</button>
            </div>
          )}
          {/* Wave B2 (2026-02) — Terms of Trade version toggle */}
          {showTermsToggle && (
            <div className="ml-3 flex items-center gap-0 rounded-md border border-slate-200 overflow-hidden" data-testid="terms-of-trade-version-toggle">
              <span className="text-[10px] font-bold uppercase tracking-wider text-slate-500 px-2">Version:</span>
              <button
                data-testid="terms-of-trade-version-full"
                onClick={() => setTermsVariant('full')}
                className={`text-xs px-3 py-1 transition-all ${termsVariant === 'full' ? 'bg-slate-700 text-white' : 'text-slate-600 hover:bg-slate-50'}`}
                title="Full multi-page Terms of Sale Agreement (default)"
              >Full</button>
              <button
                data-testid="terms-of-trade-version-simple"
                onClick={() => setTermsVariant('simple')}
                className={`text-xs px-3 py-1 transition-all ${termsVariant === 'simple' ? 'bg-slate-500 text-white' : 'text-slate-600 hover:bg-slate-50'}`}
                title="1-page condensed 5-clause variant — for smaller repair jobs"
              >Simple</button>
            </div>
          )}
        </div>
        <div className="flex items-center gap-2">
          {/* Page navigation */}
          {numPages > 1 && (
            <div className="flex items-center gap-1 mr-2">
              <button onClick={() => setPageNumber(p => Math.max(1, p - 1))} disabled={pageNumber <= 1} className="p-1 rounded hover:bg-slate-100 disabled:opacity-30"><ChevronLeft size={16} /></button>
              <span className="text-xs text-slate-600 min-w-[60px] text-center">{pageNumber} / {numPages}</span>
              <button onClick={() => setPageNumber(p => Math.min(numPages, p + 1))} disabled={pageNumber >= numPages} className="p-1 rounded hover:bg-slate-100 disabled:opacity-30"><ChevronRight size={16} /></button>
            </div>
          )}
          {/* Zoom */}
          <button onClick={() => setScale(s => Math.max(0.5, s - 0.2))} className="p-1.5 rounded hover:bg-slate-100" title="Zoom out"><ZoomOut size={15} /></button>
          <span className="text-xs text-slate-500 w-10 text-center">{Math.round(scale * 100)}%</span>
          <button onClick={() => setScale(s => Math.min(3, s + 0.2))} className="p-1.5 rounded hover:bg-slate-100" title="Zoom in"><ZoomIn size={15} /></button>
          <div className="h-5 w-px bg-slate-200 mx-1" />
          <button data-testid="pdf-viewer-refresh" onClick={loadPdf} disabled={loading} className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md border border-slate-300 text-slate-600 hover:bg-slate-50 transition-all disabled:opacity-50">
            <RefreshCw size={13} className={loading ? 'animate-spin' : ''} /> Refresh
          </button>
          {/* PDF Preview — opens the PDF in a new browser tab with no app
              chrome so the operator sees the document exactly as the
              recipient client will see it. */}
          <button
            data-testid="pdf-viewer-preview"
            onClick={handlePreview}
            disabled={!pdfData}
            className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md border border-slate-300 text-slate-700 hover:bg-slate-50 transition-all disabled:opacity-50"
            title="Open the PDF in a new tab — what the client will see"
          >
            <ExternalLink size={13} /> PDF Preview
          </button>
          {/* Edit PDF — jumps straight to the PDF Styling tab so the operator
              can tweak logo/colour/font/copy and come back to this preview
              page (a Refresh shows the new look). Currently surfaced for
              every report type so the same shortcut is reachable from any
              PDF preview view. */}
          <button
            data-testid="pdf-viewer-edit-styling"
            onClick={() => {
              // Open the styling editor in a NEW tab so the appraiser
              // doesn't lose this preview while editing.
              window.open('/report-setup?tab=pdf_styling', '_blank');
            }}
            className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md border border-[#D35400]/40 text-[#A04000] hover:bg-orange-50 hover:border-[#D35400] transition-all"
            title="Open the PDF Styling editor in a new tab (Refresh here after saving)"
          >
            <Palette size={13} /> Edit PDF
          </button>
          <button data-testid="pdf-viewer-print" onClick={handlePrint} disabled={!pdfData} className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md border border-slate-300 text-slate-600 hover:bg-slate-50 transition-all disabled:opacity-50">
            <Printer size={13} /> Print
          </button>
          <button data-testid="pdf-viewer-download" onClick={handleDownload} disabled={!pdfData} className="flex items-center gap-1.5 text-xs px-3 py-1.5 rounded-md bg-[#D35400] text-white hover:bg-[#A04000] transition-all disabled:opacity-50">
            <Download size={13} /> Download
          </button>
        </div>
      </div>

      {/* PDF Content */}
      <div ref={containerRef} className="flex-1 overflow-auto flex justify-center p-6 bg-slate-100">
        {loading && (
          <div className="flex flex-col items-center gap-3 text-slate-500 mt-20">
            <Loader2 size={32} className="animate-spin text-[#D35400]" />
            <span className="text-sm">Generating {label} Report...</span>
          </div>
        )}
        {error && (
          <div className="flex flex-col items-center gap-3 text-red-600 max-w-md text-center mt-20">
            <span className="text-sm font-medium">Failed to generate report</span>
            <span className="text-xs text-red-500">{error}</span>
            <button onClick={loadPdf} className="text-xs px-3 py-1.5 rounded bg-red-50 text-red-600 hover:bg-red-100 transition-colors">Try Again</button>
          </div>
        )}
        {pdfData && !loading && (
          <div className="shadow-xl bg-white rounded-lg overflow-hidden">
            <Document
              file={pdfData}
              onLoadSuccess={onDocumentLoadSuccess}
              onLoadError={(err) => setError('PDF render failed: ' + err.message)}
              loading={
                <div className="flex items-center gap-2 p-10 text-slate-400">
                  <Loader2 size={20} className="animate-spin" /> Rendering...
                </div>
              }
            >
              <Page
                pageNumber={pageNumber}
                scale={scale}
                renderAnnotationLayer={true}
                renderTextLayer={true}
              />
            </Document>
          </div>
        )}
      </div>
    </div>
  );
}
